import React, { useMemo, useState } from 'react';
import { Appointment, Dentist, Patient, PaymentRecord, ExpenseRecord } from '../../types';
import { Icons } from '../Icons';

interface ReportsViewProps {
  appointments: Appointment[];
  patients: Patient[];
  dentists: Dentist[];
  payments: PaymentRecord[];
  expenses: ExpenseRecord[];
  currentUserRole: 'admin' | 'secretary' | 'doctor' | 'patient';
  onCollectPayment?: () => void;
}

const formatDate = (d: Date) => d.toISOString().split('T')[0];
const addDays = (d: Date, days: number) => {
  const copy = new Date(d);
  copy.setDate(copy.getDate() + days);
  return copy;
};

type RangePreset = 'today' | 'prev7' | 'prev30' | 'custom';

export const ReportsView: React.FC<ReportsViewProps> = ({
  appointments,
  patients,
  dentists,
  payments,
  expenses,
  currentUserRole,
  onCollectPayment,
}) => {
  const todayStr = formatDate(new Date());
  const [rangePreset, setRangePreset] = useState<RangePreset>('prev7');
  const [startDate, setStartDate] = useState<string>(todayStr);
  const [endDate, setEndDate] = useState<string>(todayStr);
  const exportCsv = (filename: string, rows: string[][]) => {
    if (rows.length === 0) return;
    const csv = rows.map(r => r.map(cell => {
      if (cell.includes(',') || cell.includes('"') || cell.includes('\n')) {
        return `"${cell.replace(/"/g, '""')}"`;
      }
      return cell;
    }).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  const applyPreset = (preset: RangePreset) => {
    setRangePreset(preset);
    const base = new Date();
    if (preset === 'today') {
      const d = formatDate(base);
      setStartDate(d);
      setEndDate(d);
    } else if (preset === 'prev7') {
      setStartDate(formatDate(addDays(base, -6)));
      setEndDate(formatDate(base));
    } else if (preset === 'prev30') {
      setStartDate(formatDate(addDays(base, -29)));
      setEndDate(formatDate(base));
    }
  };
  const filteredAppointments = useMemo(() => {
    return appointments.filter(a => {
      if (startDate && a.date < startDate) return false;
      if (endDate && a.date > endDate) return false;
      return true;
    });
  }, [appointments, startDate, endDate]);

  const { todayAppts, rangeAppts, cancellations, procedureCounts, providerLoads, paymentStats, expenseStats, financialStats, filteredPayments, filteredExpenses } = useMemo(() => {
    const today = filteredAppointments.filter(a => a.date === todayStr);
    const cancelled = filteredAppointments.filter(a => a.status === 'CANCELLED' || a.status === 'NO_SHOW');

    const procCounts: Record<string, number> = {};
    filteredAppointments.forEach(a => {
      procCounts[a.type] = (procCounts[a.type] || 0) + 1;
    });

    const providerCounts: Record<string, number> = {};
    filteredAppointments.forEach(a => {
      providerCounts[a.providerId] = (providerCounts[a.providerId] || 0) + 1;
    });
    const providerLoads = Object.entries(providerCounts)
      .map(([id, count]) => ({
        id,
        name: dentists.find(d => d.id === id)?.name || 'Provider',
        count,
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    const filteredPayments = payments.filter(p => {
      const payDate = p.createdAt.split('T')[0];
      if (startDate && payDate < startDate) return false;
      if (endDate && payDate > endDate) return false;
      return true;
    });

    const paymentTotal = filteredPayments.reduce((sum, p) => sum + p.amount, 0);
    const paymentMethods = filteredPayments.reduce<Record<string, { amount: number; count: number }>>((acc, p) => {
      acc[p.method] = acc[p.method] || { amount: 0, count: 0 };
      acc[p.method].amount += p.amount;
      acc[p.method].count += 1;
      return acc;
    }, {});

    const filteredExpenses = expenses.filter(e => {
      const date = e.createdAt.split('T')[0];
      if (startDate && date < startDate) return false;
      if (endDate && date > endDate) return false;
      return true;
    });
    const expenseTotal = filteredExpenses.reduce((sum, e) => sum + e.amount, 0);
    const expenseCategories = filteredExpenses.reduce<Record<string, { amount: number; count: number }>>((acc, e) => {
      acc[e.category] = acc[e.category] || { amount: 0, count: 0 };
      acc[e.category].amount += e.amount;
      acc[e.category].count += 1;
      return acc;
    }, {});

    return {
      todayAppts: today,
      rangeAppts: filteredAppointments,
      cancellations: cancelled,
      procedureCounts: Object.entries(procCounts).sort((a, b) => b[1] - a[1]).slice(0, 5),
      providerLoads,
      paymentStats: {
        total: paymentTotal,
        count: filteredPayments.length,
        byMethod: paymentMethods,
      },
      expenseStats: {
        total: expenseTotal,
        count: filteredExpenses.length,
        byCategory: expenseCategories,
      },
      financialStats: {
        payments: paymentTotal,
        expenses: expenseTotal,
        net: paymentTotal - expenseTotal,
      },
      filteredPayments,
      filteredExpenses,
    };
  }, [filteredAppointments, payments, expenses, patients, dentists, todayStr, startDate, endDate]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="text-3xl font-black">Reports</h2>
          <p className="text-sm text-slate-500">Snapshot of key metrics for {currentUserRole === 'doctor' ? 'providers' : 'admin visibility'}.</p>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 text-xs text-slate-500">
          <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1">
            {(['today', 'prev7', 'prev30', 'custom'] as const).map(preset => (
              <button
                key={preset}
                type="button"
                onClick={() => applyPreset(preset)}
                className={`px-3 py-1 rounded-lg font-bold capitalize ${
                  rangePreset === preset ? 'bg-white shadow text-slate-800' : 'text-slate-500'
                }`}
              >
                {preset === 'prev7' ? 'Prev 7d' : preset === 'prev30' ? 'Prev 30d' : preset}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <label className="font-bold text-slate-600">Start</label>
            <input
              type="date"
              className="border rounded-lg px-2 py-1 text-sm"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setRangePreset('custom');
              }}
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="font-bold text-slate-600">End</label>
            <input
              type="date"
              className="border rounded-lg px-2 py-1 text-sm"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setRangePreset('custom');
              }}
            />
          </div>
          <span className="px-3 py-1 rounded-lg bg-blue-50 text-blue-700 font-bold">Today: {todayStr}</span>
          <span className="px-3 py-1 rounded-lg bg-slate-100 font-bold text-slate-700">In range: {rangeAppts.length}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
        <div className="p-5 bg-white rounded-3xl border shadow-sm space-y-3 animate-scaleIn">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase text-slate-400">Daily schedule</span>
            <Icons.Calendar />
          </div>
          <p className="text-3xl font-black">{todayAppts.length}</p>
          <p className="text-sm text-slate-500">Today's booked appointments.</p>
        </div>

        <div className="p-5 bg-white rounded-3xl border shadow-sm space-y-3 animate-scaleIn">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase text-slate-400">Date range</span>
            <Icons.Clock />
          </div>
          <p className="text-3xl font-black">{rangeAppts.length}</p>
          <p className="text-sm text-slate-500">Appointments in the selected range.</p>
        </div>

        <div className="p-5 bg-white rounded-3xl border shadow-sm space-y-3 animate-scaleIn">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase text-slate-400">Cancellations / No-shows</span>
            <Icons.AlertCircle />
          </div>
          <p className="text-3xl font-black">{cancellations.length}</p>
          <p className="text-sm text-slate-500">Total cancelled or no-show entries in all records.</p>
        </div>

        <div className="p-5 bg-white rounded-3xl border shadow-sm space-y-3 animate-scaleIn">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase text-slate-400">Payments</span>
            <Icons.FileText />
          </div>
          <p className="text-3xl font-black">${paymentStats.total.toLocaleString()}</p>
          <p className="text-sm text-slate-500">{paymentStats.count} collected in the selected range.</p>
        </div>

        <div className="p-5 bg-white rounded-3xl border shadow-sm space-y-3 animate-scaleIn">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase text-slate-400">Expenses</span>
            <Icons.DollarSign />
          </div>
          <p className="text-3xl font-black">${expenseStats.total.toLocaleString()}</p>
          <p className="text-sm text-slate-500">{expenseStats.count} expenses in the selected range.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-5 bg-white rounded-3xl border shadow-sm space-y-2 animate-fadeIn">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase text-slate-400">Financial summary</span>
            <Icons.FileText />
          </div>
          <div className="grid grid-cols-1 gap-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-slate-500">Collections</span>
              <span className="font-black text-emerald-700">${financialStats.payments.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-500">Expenses</span>
              <span className="font-black text-rose-700">${financialStats.expenses.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between border-t pt-2">
              <span className="text-slate-700 font-bold">Net</span>
              <span className={`font-black ${financialStats.net >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                ${financialStats.net.toLocaleString()}
              </span>
            </div>
          </div>
        </div>

        <div className="p-5 bg-white rounded-3xl border shadow-sm space-y-3 animate-fadeIn">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase text-slate-400">Download</span>
            <Icons.Download />
          </div>
          <p className="text-sm text-slate-500">Export collections and expenses for the selected range.</p>
          <button
            type="button"
            className="w-full px-4 py-3 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700"
            onClick={() => {
              const combined = [
                ...filteredPayments.map(p => ({
                  type: 'Credit',
                  date: p.createdAt.split('T')[0],
                  description: `Payment - ${patients.find(pt => pt.id === p.patientId)?.name || p.patientId} (${p.method})`,
                  amount: p.amount,
                })),
                ...filteredExpenses.map(e => ({
                  type: 'Debit',
                  date: e.createdAt.split('T')[0],
                  description: `Expense - ${e.description} (${e.category})`,
                  amount: e.amount,
                })),
              ].sort((a, b) => a.date.localeCompare(b.date));

              let balance = 0;
              const detailRows = combined.map(entry => {
                balance += entry.type === 'Credit' ? entry.amount : -entry.amount;
                return [
                  entry.type,
                  entry.date,
                  entry.description,
                  entry.amount.toFixed(2),
                  balance.toFixed(2),
                ];
              });

              const summaryRows = [
                [],
                ['Summary', `${startDate} to ${endDate}`, '', '', ''],
                ['Collections', '', '', paymentStats.total.toFixed(2), ''],
                ['Expenses', '', '', expenseStats.total.toFixed(2), ''],
                ['Net', '', '', (paymentStats.total - expenseStats.total).toFixed(2), ''],
              ];

              exportCsv('financial-report.csv', [
                ['Type', 'Date', 'Description', 'Amount', 'Balance'],
                ...detailRows,
                ...summaryRows,
              ]);
            }}
          >
            Export financial report (CSV)
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="p-5 bg-white rounded-3xl border shadow-sm animate-fadeIn">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-black">Top procedures</h3>
            <Icons.FileText />
          </div>
          {procedureCounts.length === 0 ? (
            <p className="text-sm text-slate-400">No procedures logged.</p>
          ) : (
            <div className="space-y-2">
              {procedureCounts.map(([name, count], idx) => (
                <div key={name} className="flex items-center justify-between rounded-xl border p-3 bg-slate-50">
                  <div className="flex items-center gap-3">
                    <span className="w-7 h-7 rounded-lg bg-blue-50 text-blue-700 font-black flex items-center justify-center">{idx + 1}</span>
                    <div>
                      <p className="font-bold text-slate-800">{name}</p>
                      <p className="text-xs text-slate-500">In all appointments</p>
                    </div>
                  </div>
                  <span className="px-3 py-1 rounded-lg bg-white border text-sm font-bold">{count}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-5 bg-white rounded-3xl border shadow-sm animate-fadeIn">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-black">Provider utilization</h3>
            <Icons.Users />
          </div>
          {providerLoads.length === 0 ? (
            <p className="text-sm text-slate-400">No provider activity yet.</p>
          ) : (
            <div className="space-y-2">
              {providerLoads.map(provider => (
                <div key={provider.id} className="flex items-center justify-between rounded-xl border p-3 bg-slate-50">
                  <div>
                    <p className="font-bold text-slate-800">{provider.name}</p>
                    <p className="text-xs text-slate-500">Appointments assigned</p>
                  </div>
                  <span className="px-3 py-1 rounded-lg bg-white border text-sm font-bold">{provider.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="p-5 bg-white rounded-3xl border shadow-sm animate-fadeIn">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-black">Payment breakdown</h3>
            <div className="flex items-center gap-2">
              <Icons.DollarSign />
              <button
                type="button"
                className="px-3 py-1 rounded-lg border text-xs font-bold hover:bg-slate-50"
                onClick={() => {
                  const rows = [
                    ['Method', 'Amount', 'Count'],
                    ...Object.entries(paymentStats.byMethod).map(([method, data]) => [
                      method,
                      data.amount.toString(),
                      data.count.toString(),
                    ]),
                  ];
                  exportCsv('payment_breakdown.csv', rows);
                }}
                disabled={Object.keys(paymentStats.byMethod).length === 0}
              >
                Export CSV
              </button>
            </div>
          </div>
          {Object.keys(paymentStats.byMethod).length === 0 ? (
            <p className="text-sm text-slate-400">No payments collected in this range.</p>
          ) : (
            <div className="space-y-2">
              {Object.entries(paymentStats.byMethod).map(([method, data]) => (
                <div key={method} className="flex items-center justify-between rounded-xl border p-3 bg-slate-50">
                  <div>
                    <p className="font-bold text-slate-800 capitalize">{method}</p>
                    <p className="text-xs text-slate-500">{data.count} payments</p>
                  </div>
                  <span className="px-3 py-1 rounded-lg bg-white border text-sm font-bold">${data.amount.toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-5 bg-white rounded-3xl border shadow-sm animate-fadeIn">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-black">Expense breakdown</h3>
            <div className="flex items-center gap-2">
              <Icons.FileText />
              <button
                type="button"
                className="px-3 py-1 rounded-lg border text-xs font-bold hover:bg-slate-50"
                onClick={() => {
                  const rows = [
                    ['Category', 'Amount', 'Count'],
                    ...Object.entries(expenseStats.byCategory).map(([cat, data]) => [
                      cat,
                      data.amount.toString(),
                      data.count.toString(),
                    ]),
                  ];
                  exportCsv('expense_breakdown.csv', rows);
                }}
                disabled={Object.keys(expenseStats.byCategory).length === 0}
              >
                Export CSV
              </button>
            </div>
          </div>
          {Object.keys(expenseStats.byCategory).length === 0 ? (
            <p className="text-sm text-slate-400">No expenses in this range.</p>
          ) : (
            <div className="space-y-2">
              {Object.entries(expenseStats.byCategory).map(([cat, data]) => (
                <div key={cat} className="flex items-center justify-between rounded-xl border p-3 bg-slate-50">
                  <div>
                    <p className="font-bold text-slate-800 capitalize">{cat}</p>
                    <p className="text-xs text-slate-500">{data.count} expenses</p>
                  </div>
                  <span className="px-3 py-1 rounded-lg bg-white border text-sm font-bold">${data.amount.toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>

      
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="p-5 bg-white rounded-3xl border shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-black">Payments detail</h3>
            <div className="flex items-center gap-2">
              <Icons.FileText />
              {onCollectPayment && currentUserRole !== 'patient' && (
                <button
                  type="button"
                  className="px-3 py-1 rounded-lg bg-blue-600 text-white text-xs font-bold hover:bg-blue-700 flex items-center gap-1"
                  onClick={onCollectPayment}
                >
                  <Icons.DollarSign />
                  Collect payment
                </button>
              )}
              <button
                type="button"
                className="px-3 py-1 rounded-lg border text-xs font-bold hover:bg-slate-50"
                onClick={() => {
                  const rows = [
                    ['Date', 'Patient', 'Amount', 'Method'],
                    ...filteredPayments.map(p => {
                      const patient = patients.find(pt => pt.id === p.patientId);
                      return [
                        p.createdAt.split('T')[0],
                        patient?.name || p.patientId,
                        p.amount.toString(),
                        p.method,
                      ];
                    }),
                  ];
                  exportCsv('payments_detail.csv', rows);
                }}
                disabled={filteredPayments.length === 0}
              >
                Export CSV
              </button>
            </div>
          </div>
          {filteredPayments.length === 0 ? (
            <p className="text-sm text-slate-400">No payments in this range.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-slate-600 uppercase text-[11px]">
                  <tr>
                    <th className="text-left px-3 py-2 font-bold">Date</th>
                    <th className="text-left px-3 py-2 font-bold">Patient</th>
                    <th className="text-left px-3 py-2 font-bold">Amount</th>
                    <th className="text-left px-3 py-2 font-bold">Method</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPayments.slice(0, 50).map(p => {
                    const patient = patients.find(pt => pt.id === p.patientId);
                    return (
                      <tr key={p.id} className="border-b last:border-b-0">
                        <td className="px-3 py-2">{p.createdAt.split('T')[0]}</td>
                        <td className="px-3 py-2">{patient?.name || p.patientId}</td>
                        <td className="px-3 py-2 font-bold">${p.amount.toLocaleString()}</td>
                        <td className="px-3 py-2 capitalize">{p.method}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="p-5 bg-white rounded-3xl border shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-black">Expenses detail</h3>
            <div className="flex items-center gap-2">
              <Icons.DollarSign />
              <button
                type="button"
                className="px-3 py-1 rounded-lg border text-xs font-bold hover:bg-slate-50"
                onClick={() => {
                  const rows = [
                    ['Date', 'Description', 'Category', 'Amount'],
                    ...filteredExpenses.map(e => [
                      e.createdAt.split('T')[0],
                      e.description,
                      e.category,
                      e.amount.toString(),
                    ]),
                  ];
                  exportCsv('expenses_detail.csv', rows);
                }}
                disabled={filteredExpenses.length === 0}
              >
                Export CSV
              </button>
            </div>
          </div>
          {filteredExpenses.length === 0 ? (
            <p className="text-sm text-slate-400">No expenses in this range.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-slate-600 uppercase text-[11px]">
                  <tr>
                    <th className="text-left px-3 py-2 font-bold">Date</th>
                    <th className="text-left px-3 py-2 font-bold">Description</th>
                    <th className="text-left px-3 py-2 font-bold">Category</th>
                    <th className="text-left px-3 py-2 font-bold">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredExpenses.slice(0, 50).map(e => (
                    <tr key={e.id} className="border-b last:border-b-0">
                      <td className="px-3 py-2">{e.createdAt.split('T')[0]}</td>
                      <td className="px-3 py-2">{e.description}</td>
                      <td className="px-3 py-2 capitalize">{e.category}</td>
                      <td className="px-3 py-2 font-bold">${e.amount.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
